function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import ControlButton from '../ControlButton/ControlButton';
import { triggerScreenReader } from '../utils/aria-helper';
import tscStyles from '../constants/TscStyles';
import { Close16x16 } from '../constants/Glyphs';
import FocusLock from 'react-focus-lock';
import { getClassName } from '../utils/get-class-name';
import TscButtonType from '../constants/TscButtonType';
var structure = {
  footerHeight: '3.5rem',
  headerHeight: '3rem',
  modalWidth: '22rem',
  borderRadius: tscStyles.border.radius.large
};
var animation = keyframes(["0%{transform:scale(.8);opacity:0;}50%{transform:scale(1.1);}100%{transform:scale(1);opacity:1;}"]);
var fadein = keyframes(["from{opacity:0;}to{opacity:1;}"]);
var Container = styled.div.withConfig({
  displayName: "AlertModal__Container"
})(["position:fixed;display:flex;justify-content:center;align-items:center;top:0;right:0;bottom:0;left:0;z-index:", ";color:", ";background:rgba(0,0,0,.3);animation:", " 300ms ease;animation-fill-mode:forwards;.focus-guard{position:absolute;left:0;right:0;}"], function (props) {
  return props.zIndex;
}, tscStyles.color.text.mediumDark, fadein);
var Window = styled.div.withConfig({
  displayName: "AlertModal__Window"
})(["margin:auto;width:", ";max-width:90%;max-height:90%;background:#fff;border-radius:", ";box-shadow:0 0 1rem rgba(0,0,0,.2);animation:", " 300ms  ease;"], function (props) {
  return props.modalWidth ? props.modalWidth : structure.modalWidth;
}, structure.borderRadius, animation);
var Header = styled.div.withConfig({
  displayName: "AlertModal__Header"
})(["padding:0 .5rem 0 1rem;height:", ";display:flex;align-items:center;justify-content:space-between;color:", ";background:", ";border-bottom:1px solid ", ";flex-shrink:0;border-radius:", " ", " 0 0;"], structure.headerHeight, tscStyles.color.text.mediumDark, tscStyles.color.ui.dawn.lightMedium, tscStyles.color.ui.dawn.medium, structure.borderRadius, structure.borderRadius);
var Body = styled.div.withConfig({
  displayName: "AlertModal__Body"
})(["line-height:1.4;position:relative;padding:1rem;background:", ";overflow:auto;border-bottom-right-radius:", ";border-bottom-left-radius:", ";"], tscStyles.color.ui.dawn.light, function (props) {
  return props.hideButtons ? structure.borderRadius : '0';
}, function (props) {
  return props.hideButtons ? structure.borderRadius : '0';
});
var Title = styled.h2.withConfig({
  displayName: "AlertModal__Title"
})(["padding:0;margin:0;font-size:1.125rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-weight:600;"]);
var Footer = styled.div.withConfig({
  displayName: "AlertModal__Footer"
})(["padding:0 1rem;height:", ";display:flex;align-items:center;justify-content:flex-end;color:", ";flex-shrink:0;"], structure.footerHeight, tscStyles.color.text.medium);
var ButtonGroup = styled.div.withConfig({
  displayName: "AlertModal__ButtonGroup"
})(["display:flex;align-items:center;width:100%;margin-bottom:.5rem;> *{width:100%;flex:1 1 !important;margin-right:.5rem;}> *:last-child{margin-right:0;}"]);
var defaultProps = {
  closeContent: 'Close',
  confirmButtonType: TscButtonType.secondary,
  confirmContent: 'Confirm',
  disableConfirmButton: false,
  dismissButtonType: TscButtonType.tertiary,
  dismissContent: 'Dismiss',
  enableFocusTrap: true,
  modalWidth: structure.modalWidth,
  title: 'Alert',
  zIndex: 9999
};

var AlertModal = function AlertModal(props) {
  var _defaultProps$props = _objectSpread(_objectSpread({}, defaultProps), props),
      children = _defaultProps$props.children,
      className = _defaultProps$props.className,
      closeContent = _defaultProps$props.closeContent,
      confirmButtonType = _defaultProps$props.confirmButtonType,
      confirmContent = _defaultProps$props.confirmContent,
      disableConfirmButton = _defaultProps$props.disableConfirmButton,
      dismissButtonType = _defaultProps$props.dismissButtonType,
      dismissContent = _defaultProps$props.dismissContent,
      enableFocusTrap = _defaultProps$props.enableFocusTrap,
      hideButtons = _defaultProps$props.hideButtons,
      modalWidth = _defaultProps$props.modalWidth,
      onClose = _defaultProps$props.onClose,
      onConfirm = _defaultProps$props.onConfirm,
      onDismiss = _defaultProps$props.onDismiss,
      testId = _defaultProps$props.testId,
      title = _defaultProps$props.title,
      zIndex = _defaultProps$props.zIndex;

  React.useEffect(function () {
    var modalOpenedEvent = new Event(tscStyles.events.modalOpenedOnPage);
    window.dispatchEvent(modalOpenedEvent);
    triggerScreenReader("".concat(title, " alert has been opened"));
    return function () {
      triggerScreenReader("".concat(title, " alert has been closed"));
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // on mount and unmount only

  var _confirmButton = function _confirmButton() {
    if (typeof onConfirm === 'function') {
      var confirmTestId = testId ? "".concat(testId, "-confirm-button") : '';
      return _jsx(ControlButton, {
        className: getClassName(className, 'confirm'),
        buttonType: confirmButtonType,
        isFullWidth: true,
        label: confirmContent,
        testId: confirmTestId,
        disabled: disableConfirmButton,
        onClick: onConfirm
      });
    }

    return null;
  };

  var _dismissButton = function _dismissButton() {
    if (typeof onDismiss === 'function') {
      var dismissButtonTestId = testId ? "".concat(testId, "-dismiss-button") : '';
      return _jsx(ControlButton, {
        className: getClassName(className, 'dismiss'),
        buttonType: dismissButtonType,
        isFullWidth: true,
        label: dismissContent,
        testId: dismissButtonTestId,
        onClick: onDismiss
      });
    }

    return null;
  };

  var _closeModalButton = function _closeModalButton(glyph) {
    var closeModalButtonTestId = testId ? "".concat(testId, "-close-button") : '';
    return _jsx(ControlButton, {
      className: getClassName(className, 'close'),
      buttonType: TscButtonType.tertiary,
      title: closeContent,
      glyph: glyph,
      testId: closeModalButtonTestId,
      onClick: onClose
    });
  };

  var closeModalGlyphElement = _jsx(Close16x16, {});

  return _jsx(Container, {
    className: getClassName(className, null, 'alert-modal-container'),
    zIndex: zIndex,
    onClick: function onClick(e) {
      return e.stopPropagation();
    },
    children: _jsx(FocusLock, {
      className: getClassName(className, 'focus-lock', 'focus-guard'),
      disabled: !enableFocusTrap,
      children: _jsxs(Window, {
        className: getClassName(className, 'alert-modal-window'),
        modalWidth: modalWidth,
        children: [_jsxs(Header, {
          className: getClassName(className, 'alert-modal-header'),
          children: [_jsx(Title, {
            className: getClassName(className, 'alert-modal-title'),
            children: title
          }), !hideButtons && _closeModalButton(closeModalGlyphElement)]
        }), _jsx(Body, {
          className: getClassName(className, 'alert-modal-body'),
          hideButtons: hideButtons,
          children: children
        }), !hideButtons && _jsx(Footer, {
          className: getClassName(className, 'alert-modal-footer'),
          children: _jsxs(ButtonGroup, {
            className: getClassName(className, 'button-group'),
            children: [_dismissButton(), _confirmButton()]
          })
        })]
      })
    })
  });
};

AlertModal.defaultProps = defaultProps;
var AlertModalTest = {
  Container: Container,
  Window: Window,
  Header: Header,
  Body: Body,
  Title: Title,
  Footer: Footer,
  ButtonGroup: ButtonGroup
};
export { AlertModal, AlertModalTest };
export default AlertModal;